import type { ApiEnvelope } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1/clienthubmanagement';

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  accessToken?: string | null;
  sessionId?: string | null;
}

/**
 * Interface language for every hub request. It travels as a header for the
 * Lambdas and as a query parameter so the CloudFront cache key varies by
 * language (the API cache policy forwards query strings, not headers).
 */
let apiLocale = 'en';

export function setApiLocale(locale: string) {
  apiLocale = locale;
}

function withLocale(path: string): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}lang=${encodeURIComponent(apiLocale)}`;
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, auth = true, accessToken, sessionId } = options;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': apiLocale,
  };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (auth && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (sessionId) {
    headers['X-Session-Id'] = sessionId;
  }

  const response = await fetch(`${API_BASE}${withLocale(path)}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let payload: ApiEnvelope<T> | null = null;
  const text = await response.text();

  if (text) {
    try {
      payload = JSON.parse(text) as ApiEnvelope<T>;
    } catch {
      if (!response.ok) {
        throw new ApiError(text || response.statusText, response.status);
      }
      return text as unknown as T;
    }
  }

  if (!response.ok) {
    const data = payload?.data as { code?: string } | undefined;
    throw new ApiError(
      payload?.message || response.statusText,
      response.status,
      data?.code,
    );
  }

  return (payload?.data ?? {}) as T;
}

/**
 * Authenticated binary fetch for library media proxies (thumbnail / motion clip).
 * Returns a blob suitable for `URL.createObjectURL`.
 */
export async function fetchMediaBlob(
  path: string,
  options: Pick<RequestOptions, 'accessToken' | 'sessionId'> = {},
): Promise<Blob> {
  const { accessToken, sessionId } = options;
  const headers: Record<string, string> = {
    Accept: 'image/*,*/*',
    'Accept-Language': apiLocale,
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (sessionId) {
    headers['X-Session-Id'] = sessionId;
  }

  const response = await fetch(`${API_BASE}${path}`, { method: 'GET', headers });
  if (!response.ok) {
    throw new ApiError(response.statusText || 'Media request failed', response.status);
  }
  return response.blob();
}

export { API_BASE };
